import { z } from 'zod'

// Match the PriceLineItem interface from price-line-items-selector
const priceLineItemSchema = z.object({
    productId: z.string(),
    productName: z.string(),
    variantId: z.string(),
    variantName: z.string(),
    imageUrl: z.string().nullable(),
    priceId: z.string(),
    quantity: z.number().min(1),
})

// Trigger schema - includes optional display fields for UI
const triggerSchema = z.object({
    productId: z.string().nullable(),
    productName: z.string().optional(),
    imageUrl: z.string().nullable().optional(),
    type: z.enum(['any', 'product']).default('any'),
})

// CurrencyOptions schema for non-interactive rules
const currencyOptionSchema = z.object({
    rate: z.number(),
    amount: z.number(),
    lock: z.boolean(),
    date: z.string(),
})

const currencyOptionsSchema = z.record(z.string(), currencyOptionSchema)

// Non-interactive rule schema
export const nonInteractiveRuleSchema = z.object({
    type: z.enum([
        'CartContainsProduct',
        'CartTotalAbove',
        'CartTotalBelow',
        'CartItemCountAbove',
        'CartItemCountBelow',
    ]),
    productId: z.string().optional(),
    minimumAmount: currencyOptionsSchema.optional(),
    itemCount: z.number().optional(),
})

export type NonInteractiveRule = z.infer<typeof nonInteractiveRuleSchema>

// Order Bump Offer schema (for type='orderbump')
const orderBumpOfferSchema = z.object({
    // The stored row's id. Without it here, zodResolver stripped it from the
    // validated payload, the server saw every offer as new and re-created all of
    // them on each save — new ids, so every funnel step's enabledOfferIds pointed
    // at deleted rows and the bump silently stopped doing anything (#TAG-2600).
    id: z.string().optional(),
    productId: z.string(),
    variantId: z.string().nullable(),
    priceId: z.string().nullable(),
    type: z.enum(['primary', 'secondary', 'vip', 'non_interactive']),
    precheck: z.boolean(),
    quantity: z.number().min(1).max(100).default(1),
    titleTrans: z.record(z.string(), z.string()).default({}),
    descriptionTrans: z.record(z.string(), z.string()).default({}),
    overrideImageUrl: z.string().nullable().optional(),
    displayPrice: z.boolean().default(true),
    displayCompareAtPrice: z.boolean().default(false),
    compareAtPriceDiscount: z.number().min(0).max(100).default(20),
    rules: z.array(nonInteractiveRuleSchema).default([]),
})

export type OrderBumpOffer = z.infer<typeof orderBumpOfferSchema>

// Post-Purchase Upsell Offer schema (for type='upsell')
const upsellOfferSchema = z.object({
    productId: z.string(),
    variantId: z.string().nullable(),
    priceId: z.string().nullable(),
    title: z.string(),
    titleTrans: z
        .record(z.string(), z.string())
        .default({})
        .refine((val) => val.en && val.en.length > 0, {
            message: 'form.validation.englishTranslationRequired',
        }),
})

export type UpsellOffer = z.infer<typeof upsellOfferSchema>

export const upsellFormSchema = z.object({
    offerTitle: z.string().min(1, 'form.validation.titleRequired'),
    enabled: z.boolean(),
    type: z.enum(['orderbump', 'upsell']),
    triggerType: z.enum(['any', 'product']).default('any'),
    triggers: z.array(triggerSchema),
    orderBumpOffers: z.array(orderBumpOfferSchema),
    offers: z.array(upsellOfferSchema),
}).superRefine((data, ctx) => {
    // Validate triggers based on triggerType
    if (data.triggerType === 'product' && data.triggers.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'form.validation.triggersRequired',
            path: ['triggers'],
        });
    }

    // Validate offers based on type
    if (data.type === 'orderbump') {
        if (data.orderBumpOffers.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'form.validation.orderBumpOffersRequired',
                path: ['orderBumpOffers'],
            });
        }
        if (data.orderBumpOffers.length > 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'form.validation.maxOrderBumps',
                path: ['orderBumpOffers'],
            });
        }
    } else if (data.type === 'upsell') {
        if (data.offers.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'form.validation.upsellOffersRequired',
                path: ['offers'],
            });
        }
        if (data.offers.length > 3) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'form.validation.maxUpsellOffers',
                path: ['offers'],
            });
        }
    }
})

export type UpsellFormData = z.infer<typeof upsellFormSchema>
